'use client';

import React from 'react';
import { styled } from '@linaria/react';

import { MAX_NUM_OF_LIKES } from '@/constants';
import useSound from '@/hooks/use-sound';

import { useContent } from '@/components/ContentProvider';

import LikeButton from './LikeButton';
import LikeButtonParticles from './LikeButtonParticles';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  size?: number;
}

function ConnectedLikeButton({ size = 48, ...delegated }: Props) {
  const {
    metrics,
    actions: { toggleLikeButton },
  } = useContent();

  const [playFill] = useSound('/sounds/glug-b.mp3', {
    interrupt: true,
    volume: 0.25,
  });
  const [playPop] = useSound('/sounds/pop.mp3', { volume: 0.5 });

  if (typeof metrics.totalNumLikesFromUser !== 'number') {
    return null;
  }

  const totalNumLikesFromUser =
    metrics.totalNumLikesFromUser as number;

  return (
    <Wrapper {...delegated}>
      <LikeButtonParticles
        size={size}
        totalNumLikesFromUser={totalNumLikesFromUser}
      />
      <LikeButton
        size={size}
        totalNumLikesFromUser={totalNumLikesFromUser}
        // If the user has previously maxed out the like button, the heart will bounce happily on mount, which is cute, but will happen on mount every single time, whenever they refresh the page or revisit an article. This would get annoying quick. So instead, only enable the bounce animation after user interaction.
        enableBounceOnMaxout={metrics.lastInteractionAt !== 0}
        onLike={() => {
          if (totalNumLikesFromUser < MAX_NUM_OF_LIKES) {
            const newNumOfLikes = toggleLikeButton('increment');

            playFill({
              playbackRate:
                (newNumOfLikes - 1) / (MAX_NUM_OF_LIKES - 1) + 0.9,
            });

            if (newNumOfLikes === MAX_NUM_OF_LIKES) {
              playPop();
            }
          }
        }}
        onUnlike={() => {
          if (totalNumLikesFromUser > 0) {
            const newNumOfLikes = toggleLikeButton('decrement');

            playFill({
              playbackRate:
                (newNumOfLikes - 1) / (MAX_NUM_OF_LIKES - 1) + 0.9,
            });
          }
        }}
        // We want to retrigger the "MAX" label whenever they click a maxed-out like button
        onMaxLike={() => toggleLikeButton('increment')}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
`;

export default ConnectedLikeButton;
